import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Typography, Flex } from 'antd';
import { setHeroTitle, setHeroContent, setHeroImage } from '../redux/hero.slice'
const { Title, Paragraph } = Typography;

import Conatiner from '../shared/Conatiner/Container';
import { SUPPORT_EMAIL, SUPPORT_PORTAL_URL } from '../shared/supportDetails';

import useMobileCheck from '../mobile';

import Markdown from 'react-markdown'
import './Policy.css';

const PegboardPage = () => {
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(setHeroImage(false));
    dispatch(setHeroTitle(`Privacy, Done Right`))
    dispatch(setHeroContent(`We believe trust is the foundation of great clubs. That’s why your data stays safe, secure, and in your control. No hidden sharing, no surprises—just straightforward protection.`))
  }, [dispatch]);

  const { isMobile } = useMobileCheck();

  const lastUpdate = '2 September 2026';
  const privacy = `
#### 1. Introduction

ShuttleOps (“we”, “us”, “our”) is committed to protecting your privacy. This Privacy Policy explains what personal information we collect, why we collect it, how we use it, who we share it with, and your rights in relation to that information.

Please read this policy carefully. If you have any questions or concerns, contact us at **${SUPPORT_EMAIL}**.

#### 2. Information we collect

##### Authentication information
We use **Kinde** for secure authentication. For this purpose we collect and store only:
- **Name**
- **Email address**

These items are used solely to authenticate and identify you in the ShuttleOps platform.

##### Club-managed information
Official club representatives (owners, admins) may add information about players for the purpose of running the club (sessions, attendance, payments, communications). Typical items added by clubs include:
- Player name
- Player email
- Player membership status and role
- Session bookings, attendance, and playing history
- Club roles, payment status, and communication preferences
- Club locations and session details

**Important:** Club representatives should obtain the individual’s consent before adding personal information. Individuals can also add their own information directly.

##### Payment / banking information
If you provide bank details for UK Direct Debit, those details (e.g., account number and sort code) are transmitted **directly to Stripe** for payment processing. ShuttleOps **does not store** full bank account details.

#### 3. How we use your information

We use the information we collect to:
- Provide secure login and account access.
- Allow clubs to manage players, sessions, attendance, and communications.
- Facilitate payments and subscriptions via Stripe.
- Send essential account- and payment-related messages (e.g., welcome emails, session notifications, payment receipts).

We **do not** use your information for third-party marketing or sell your data.

#### 4. Who we share information with

We only share your information when necessary to operate the service or where you have consented:

- **Your club administrators:** When you join or are added to a club, basic contact info (typically name and email) will be available to the club’s admins so they can manage sessions and communicate with members.
- **Stripe:** We use Stripe to process payments. Financial/banking details needed for Direct Debit are passed to Stripe and processed by them according to their policies.
- **Kinde:** We use Kinde for authentication; Kinde handles the secure login flow and provides us with the name and email used for your account.
- **Google Cloud:** We use Google Cloud to host ShuttleOps infrastructure.
- **Google Maps:** We use Google Maps for club and session location features.
- **PostHog and Brevo:** We use these services to understand product and website use, including campaign visits.
- **Email providers:** We use email services to deliver account, session, payment, and support messages.
- **Frappe Cloud:** We use Frappe Cloud for the ShuttleOps support portal.
- **Service providers and processors:** We may share limited data with third-party service providers who help us run the platform (e.g., hosting, analytics). These providers act only on our instructions and are contractually bound to protect your data.

We will not sell your personal information to third parties.

#### 5. Data security

We implement industry-standard technical and organisational measures to protect personal data against accidental loss, destruction, or unauthorised access. Examples include encrypted connections (HTTPS), access controls, and secure third-party processors (Kinde and Stripe).

**Note:** Sensitive banking details submitted for Direct Debit are handled by Stripe. ShuttleOps never stores full bank account numbers or sort codes.

#### 6. Data retention

We retain personal information only as long as it is necessary for the purposes described in this policy, or as required by law. If you request deletion of your account and data we will remove or anonymise your personal data subject to legal or contractual restrictions (for example, records we are required to keep for accounting or tax purposes).

#### 7. Your rights

Depending on your jurisdiction, you may have rights regarding your personal data, including to:
- **Access** the personal data we hold about you.
- **Correct** inaccurate or incomplete data.
- **Delete** your personal data (subject to legal restrictions).
- **Withdraw consent** where processing is based on consent.
- **Object** to or restrict certain processing activities.
- **Request a copy** or a portable version of your data.

To exercise these rights, email **${SUPPORT_EMAIL}** or use the [ShuttleOps Support Portal](${SUPPORT_PORTAL_URL}). We will respond in accordance with applicable law.

#### 8. Children

ShuttleOps is intended for clubs and players. If you are a club admin adding players under the age of 16, you must obtain parental or guardian consent before adding that player’s personal information. If a parent or guardian believes their child’s information has been added without consent, please contact us to request removal.

#### 9. Changes to this policy

We may update this Privacy Policy from time to time. When we make changes, we will revise the “Last updated” date at the top of this page and, where appropriate, notify users of material changes.

#### 10. Contact us

If you have questions, requests, or concerns about this Privacy Policy or how we process personal data, please contact:

**ShuttleOps Support**  
Email: **${SUPPORT_EMAIL}**

Support portal: [Open the ShuttleOps Support Portal](${SUPPORT_PORTAL_URL})

**End of policy**`

  return (
    <Flex vertical
      className="policy-page"
      style={{
        paddingTop: 20,
        paddingBottom: 20
      }}
      gap={20}>


      <Conatiner
        colour="white"
      >
        <Flex
          className="policy-summary"
          style={{
            padding: isMobile ? '20px' : '32px'
          }}
          vertical
          gap={10}
        >
          <Title
            level={3}
            style={{ marginTop: 0 }}
          >Quick Summary: Your Privacy at ShuttleOps</Title>
          <ul>
            <li>We only collect your name and email for login, using Kinde for secure authentication.</li>
            <li>Clubs may add your details so you can join sessions, but they must have your permission (or you can add your own info).</li>
            <li>If you pay or receive money, your bank details go straight to Stripe for processing — ShuttleOps never stores them.</li>
            <li>Your contact info is visible to your club admins, but not shared with anyone else.</li>
            <li>Service providers process limited data needed to run ShuttleOps.</li>
            <li>We don’t sell or share your data for marketing — ever.</li>
            <li>You can ask to see, update, or delete your information at any time.</li>
            <li>Email {SUPPORT_EMAIL} or use the ShuttleOps Support Portal for privacy requests.</li>
          </ul>
          <Paragraph>
            That’s it. Simple, secure, and transparent.
          </Paragraph>
        </Flex>
      </Conatiner>


      <Conatiner
        colour="white"
      >
        <Flex
          style={{
            padding: isMobile ? '20px' : '40px'
          }}
          vertical
          gap={10}
          className={"privacyPolicy policy-copy"}
        >
          <Title
            level={3}
            style={{ marginTop: 0 }}
          >ShuttleOps Privacy Policy: the detail behind your privacy.</Title>
          <Paragraph>Last updated: { lastUpdate }</Paragraph>
          <Markdown
            components={{
              // Map `h1` (`# heading`) to use `h2`s.
              h4: (props) => { 
                return <Title level={3} style={{ marginBottom: 0 }}>{ props.children }</Title>
              },
              h5: (props) => { 
                return <Title level={4} style={{ marginBottom: 0 }}>{ props.children }</Title>
              },
              ul: (props) => { 
                return <ul style={{ marginBottom: 0, marginTop: 0 }}>{ props.children }</ul>
              },
              // Rewrite `em`s (`*like so*`) to `i` with a red foreground color.
              p: (props) => {
                return <Paragraph style={{ marginBottom: 0, marginTop: 0 }}>{ props.children }</Paragraph>
              }
            }}
          >
            { privacy }
          </Markdown>
        </Flex>
      </Conatiner>
    </Flex>
  )
}

export default PegboardPage;
